import { matchPath } from 'react-router';
import { all, call, fork, put, takeLatest, select } from 'redux-saga/effects';

import * as CustomersActions from 'actions/customerActions';
import { navigateToCustomerDashboard, navigateToCustomerDetails } from 'actions/navigateActions';
import * as OfferActions from 'actions/offerActions';
import { DEFAULT_LINKED_INTERVENTION_PARAMS, OPTIONS } from 'components/customers/details/CustomerFiles/customer.files.utils';
import { restClient } from 'config/store/configureStore';
import * as types from 'constants/ActionTypes';
import { PATH } from 'constants/RoutesConstants';
import { scopeToPage } from 'modules/app/sagas/utils';
import { genericFetchInterventions } from 'modules/interventions/actions/core';
import { createNotification } from 'modules/toast-notifications/actions';
import { getCurrentUserExtensions, getCustomName } from 'modules/users/selectors/currentUser';
import { getCustomer } from 'selectors/customerSelectors';
import { getCurrentLocation } from 'selectors/router';
import { downloadFile } from 'services/Utils';

const checkDataIsEmpty = data => Object.keys(data.content?.[0] ?? {}).includes('collectionValue');

/* GET ALL CUSTOMERS */
function* fetchCustomers({ payload, meta }) {
  const { request } = payload;
  try {
    const { data } = yield call(restClient.request, request);
    yield put(CustomersActions.fetchCustomersSuccess(data, meta));
  } catch (e) {
    yield put(CustomersActions.fetchCustomersFailure(e, meta));
  }
}

function* fetchCustomersWatcher() {
  yield takeLatest(types.FETCH_CUSTOMERS_REQUEST, fetchCustomers);
}

function* fetchCustomersSelect({ payload, meta }) {
  const { request } = payload;
  try {
    const { data } = yield call(restClient.request, request);
    yield put(CustomersActions.fetchCustomersSelectSuccess(data, meta));
  } catch (e) {
    yield put(CustomersActions.fetchCustomersSelectFailure(e, meta));
  }
}

function* fetchCustomersSelectWatcher() {
  yield takeLatest(types.FETCH_CUSTOMERS_SELECT_REQUEST, fetchCustomersSelect);
}

/* GET SPECIFIC CUSTOMER */
function* fetchCustomer({ payload, meta }) {
  const { request } = payload;
  try {
    const { data: customer } = yield call(restClient.request, request);
    yield put(CustomersActions.fetchCustomerSuccess(customer, meta));
  } catch (e) {
    yield put(CustomersActions.fetchCustomerFailure(e, meta));
  }
}

function* fetchCustomerWatcher() {
  yield takeLatest(types.FETCH_CUSTOMER_REQUEST, fetchCustomer);
}

/* GET SPECIFIC CUSTOMER FOR SELECT */
function* fetchCustomerSelect({ payload, meta }) {
  const { request } = payload;
  try {
    const { data: customer } = yield call(restClient.request, request);
    yield put(CustomersActions.fetchCustomerSelectSuccess(customer, meta));
  } catch (e) {
    yield put(CustomersActions.fetchCustomerSelectFailure(e, meta));
  }
}

function* fetchCustomerSelectWatcher() {
  yield takeLatest(types.FETCH_CUSTOMER_SELECT_REQUEST, fetchCustomerSelect);
}

function* createCustomer({ payload, meta }) {
  const { createFromOffer, cancelRedirect = false, notify = true } = meta;
  const { request, additionalData } = payload;
  try {
    const response = yield call(restClient.request, request);
    yield put(CustomersActions.createCustomerSuccess(response.data, meta));

    if (notify) {
      yield put(
        createNotification({
          type: 'success',
          message: {
            id: 'page.customer.successSaving',
            defaultMessage: 'The customer has been saved.',
          },
        })
      );
    }

    if (createFromOffer) {
      yield put(OfferActions.setModalValue({ ...additionalData, customerId: response.data.id }, meta));
    } else if (!cancelRedirect) {
      yield call(navigateToCustomerDetails, response.data.id);
    }
  } catch (e) {
    yield put(CustomersActions.createCustomerFailure(e, meta));
    yield put(
      createNotification({
        type: 'error',
        message: {
          id: 'page.customer.failureSaving',
          defaultMessage: 'An error occurred while saving the customer.',
        },
      })
    );
  }
}

function* createCustomerWatcher() {
  yield takeLatest(types.CREATE_CUSTOMER_REQUEST, createCustomer);
}

function* updateCustomer({ payload, meta }) {
  const { request } = payload;
  try {
    const response = yield call(restClient.request, request);
    yield put(CustomersActions.updateCustomerSuccess(response.data, meta));
    yield put(
      createNotification({
        type: 'success',
        message: {
          id: 'page.customer.successSaving',
          defaultMessage: 'The customer has been saved.',
        },
      })
    );
  } catch (e) {
    yield put(CustomersActions.updateCustomerFailure(e, meta));
    yield put(
      createNotification({
        type: 'error',
        message: {
          id: 'page.customer.failureSaving',
          defaultMessage: 'An error occurred while saving the customer.',
        },
      })
    );
  }
}

function* updateCustomerWatcher() {
  yield takeLatest(types.UPDATE_CUSTOMER_REQUEST, updateCustomer);
}

function* deleteCustomer({ payload, meta }) {
  const { request } = payload;
  try {
    yield call(restClient.request, request);
    yield put(CustomersActions.deleteCustomerSuccess(meta));
    yield call(navigateToCustomerDashboard);
    yield put(
      createNotification({
        type: 'success',
        message: {
          id: 'page.customer.successDelete',
          defaultMessage: 'The customer has been deleted.',
        },
      })
    );
  } catch (e) {
    yield put(CustomersActions.deleteCustomerFailure(e, meta));
    yield put(
      createNotification({
        type: 'error',
        message: {
          id: 'page.customer.failureDelete',
          defaultMessage: 'An error occurred while deleting the customer.',
        },
      })
    );
  }
}

function* deleteCustomerWatcher() {
  yield takeLatest(types.DELETE_CUSTOMER_REQUEST, deleteCustomer);
}

const downloadCustomerFile = async ({ interventions, audits, name, customer, filenames }) => {
  const { default: Jszip } = await import('jszip');
  const zipFile = new Jszip();

  zipFile.file(filenames.customer, JSON.stringify(customer));

  if (interventions && !checkDataIsEmpty(interventions)) {
    zipFile.file(filenames.interventions, JSON.stringify(interventions));
  }

  if (audits && !checkDataIsEmpty(audits)) {
    zipFile.file(filenames.audits, JSON.stringify(audits));
  }

  try {
    const base64 = await zipFile.generateAsync({ type: 'base64' });
    downloadFile(base64, {
      type: 'application/zip',
      fileName: `customer${customer.firstName ? `_${customer.firstName}` : ''}${customer.lastName ? `_${customer.lastName}` : ''
      }.zip`,
    });
  } catch {
    createNotification({
      type: 'error',
      message: {
        id: 'page.customer.export.error',
        defaultMessage: 'Please contact {name} team.',
        values: { name },
      },
    });
  }
};

function* exportCustomer({ payload, meta }) {
  const { requests, customerId, filenames } = payload;
  const { interventionRequest, auditRequest } = requests;
  try {
    const [interventions, audits] = yield all([
      call(restClient.request, interventionRequest),
      call(restClient.request, auditRequest),
    ]);
    const name = yield select(getCustomName);
    const customer = yield select(state => getCustomer(state, customerId));
    yield call(downloadCustomerFile, {
      interventions: interventions?.data,
      audits: audits?.data,
      name,
      customer,
      filenames,
    });
    yield put(CustomersActions.exportCustomerSuccess(meta));
  } catch (e) {
    yield put(CustomersActions.exportCustomerFailure(e, meta));
  }
}

function* exportCustomerWatcher() {
  yield takeLatest(types.EXPORT_CUSTOMER_REQUEST, exportCustomer);
}

function* onCustomerPage() {
  const location = yield select(getCurrentLocation);
  const {
    params: { customerId },
  } = matchPath(PATH.CUSTOMERS_DETAILED.path, location);

  yield put(CustomersActions.fetchCustomer(customerId));

  const extensions = yield select(getCurrentUserExtensions);
  const availableExtension = Object.entries(OPTIONS).find(([, extension]) => extensions.includes(extension));
  if (availableExtension) {
    yield put(
      genericFetchInterventions(
        { ...DEFAULT_LINKED_INTERVENTION_PARAMS, customerId, mode: availableExtension[0], page: 0 },
        { source: 'customer-interventions' }
      )
    );
  }
}

function* scopeToPageWatcher() {
  yield scopeToPage([PATH.CUSTOMERS_DETAILED], onCustomerPage);
}

const customersSagas = [
  fork(scopeToPageWatcher),
  fork(fetchCustomersWatcher),
  fork(fetchCustomerWatcher),
  fork(fetchCustomersSelectWatcher),
  fork(createCustomerWatcher),
  fork(fetchCustomerSelectWatcher),
  fork(updateCustomerWatcher),
  fork(deleteCustomerWatcher),
  fork(exportCustomerWatcher),
];

export default customersSagas;
